import React, { useState } from 'react';
import ImageSwitcher from './ImageSwitcher';

/**
 * ContentMedia - Shared component for displaying model/workflow media content
 * 
 * Hierarchy:
 * 1. Video/GIF (highest priority)
 * 2. Multiple images -> ImageSwitcher (switches between images on interval)
 * 3. Single image fallback
 * 
 * @param {Object} props
 * @param {string} [props.videoUrl] - Video/GIF URL - highest priority
 * @param {string} [props.coverImage] - Primary cover image URL
 * @param {string} [props.cover2Image] - Secondary cover image URL
 * @param {string} [props.thumbnailImage] - Thumbnail image URL - lowest priority fallback
 * @param {Object} [props.style] - Additional styles for the container
 * @param {string} [props.className] - CSS class name
 */
export default function ContentMedia({
  videoUrl,
  coverImage,
  cover2Image,
  thumbnailImage,
  style = {},
  className = '',
}) {
  const [mediaError, setMediaError] = useState(false);

  // Check if URL is a video format (mp4, webm, mov)
  const isVideoFormat = (url) => {
    if (!url) return false;
    const lower = url.toLowerCase();
    return lower.endsWith('.mp4') || lower.endsWith('.webm') || lower.endsWith('.mov');
  };

  // Get fallback image if primary media fails
  const fallbackImage = coverImage || cover2Image || thumbnailImage;

  // Rule 1: If has video/animated URL, show it (takes priority over cover images)
  if (videoUrl && !mediaError) {
    // Video files (mp4, webm, mov) - use video tag
    if (isVideoFormat(videoUrl)) {
      return (
        <video
          src={videoUrl}
          autoPlay
          loop
          muted
          playsInline
          className={className}
          onError={() => setMediaError(true)}
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'cover', 
            display: 'block',
            ...style 
          }}
        />
      );
    }
    // Image-based formats (webp, gif, png, jpg) - use img tag
    // Animated webp/gif will auto-play in img tag
    return (
      <img
        src={videoUrl}
        alt=""
        className={className}
        onError={() => setMediaError(true)}
        style={{ 
          width: '100%', 
          height: '100%', 
          objectFit: 'cover', 
          display: 'block',
          ...style 
        }}
      />
    );
  }

  // Rule 2: If has both cover images, show ImageSwitcher
  if (coverImage && cover2Image) {
    return (
      <ImageSwitcher
        images={[coverImage, cover2Image]}
        interval={3000}
        className={className}
        style={style}
      />
    );
  }

  // Rule 3: Show available image in priority order
  if (fallbackImage) {
    return (
      <img
        src={fallbackImage}
        alt=""
        className={className}
        style={{ 
          width: '100%', 
          height: '100%', 
          objectFit: 'cover', 
          display: 'block',
          ...style 
        }}
      />
    );
  }

  // No media available - show fallback icon
  return (
    <div 
      className={className}
      style={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)',
        ...style 
      }}
    >
      <svg 
        width="40" 
        height="40" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="rgba(255,255,255,0.25)"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <polyline points="21 15 16 10 5 21" />
      </svg>
    </div>
  );
}
